import React, { useState, useCallback, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import InputAdornment from "@material-ui/core/InputAdornment";
import Icon from "@material-ui/core/Icon";
import CircularProgress from "@material-ui/core/CircularProgress";
import Alert from "@material-ui/lab/Alert";
import Snackbar from "@material-ui/core/Snackbar";
import Email from "@material-ui/icons/Email";
import CheckCircle from "@material-ui/icons/CheckCircle";
import Error from "@material-ui/icons/Error";
import Warning from "@material-ui/icons/Warning";
import Info from "@material-ui/icons/Info";

// Components
import Button from "components/CustomButtons/Button";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardFooter from "components/Card/CardFooter";
import CardHeader from "components/Card/CardHeader";
import CustomInput from "components/CustomInput/CustomInput";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";

// Utils
import apims from "apims/";
import { customApp } from "functions/";

// Styles
import styles from "assets/jss/material-dashboard-pro-react/views/loginPageStyle";

const useStyles = makeStyles((theme) => ({
    ...styles,
    // Container principal com melhor centralização
    recoveryContainer: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(2),
        zIndex: 9999,
        backdropFilter: 'blur(5px)',
        backgroundColor: 'rgba(102, 126, 234, 0.3)',
        cursor: 'pointer', // Cursor de pointer para indicar que é clicável para fechar
        '& *': {
            userSelect: 'auto', // Permite seleção de texto
        }
    },
    // Card principal com melhor design
    recoveryCard: {
        position: 'relative',
        zIndex: 10000,
        background: '#ffffff',
        borderRadius: '16px',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1), 0 6px 20px rgba(0, 0, 0, 0.15)',
        overflow: 'hidden',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        maxWidth: '450px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        cursor: 'default', // Cursor normal dentro do card
        '& input, & textarea, & [contenteditable]': {
            userSelect: 'text !important',
            pointerEvents: 'auto !important',
            cursor: 'text !important'
        }
    },
    // Header melhorado
    recoveryHeader: {
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: '#ffffff',
        padding: theme.spacing(3),
        textAlign: 'center',
        position: 'relative',
        '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '60px',
            height: '4px',
            background: 'rgba(255, 255, 255, 0.3)',
            borderRadius: '2px'
        }
    },
    recoveryTitle: {
        fontSize: '1.5rem',
        fontWeight: '600',
        margin: 0,
        textShadow: '0 1px 3px rgba(0, 0, 0, 0.2)'
    },
    recoverySubtitle: {
        fontSize: '0.9rem',
        opacity: 0.9,
        margin: theme.spacing(1, 0, 0, 0),
        fontWeight: '400'
    },
    // Body com melhor padding
    recoveryBody: {
        padding: theme.spacing(3, 3, 2, 3),
        position: 'relative',
        zIndex: 1,
        '& *': {
            position: 'relative'
        }
    },
    // Footer com melhor espaçamento
    recoveryFooter: {
        padding: theme.spacing(1, 3, 3, 3)
    },
    // Indicador de estado melhorado
    stepIndicator: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        margin: theme.spacing(2, 0, 3, 0),
        gap: theme.spacing(1.5)
    },
    stateMessage: {
        textAlign: 'center',
        color: '#555',
        fontSize: '0.95rem',
        lineHeight: 1.5,
        maxWidth: '300px'
    },
    // Ícones com melhor design
    successIcon: {
        color: '#4caf50',
        fontSize: '2.5rem',
        filter: 'drop-shadow(0 2px 4px rgba(76, 175, 80, 0.3))'
    },
    errorIcon: {
        color: '#f44336',
        fontSize: '2.5rem',
        filter: 'drop-shadow(0 2px 4px rgba(244, 67, 54, 0.3))'
    },
    warningIcon: {
        color: '#ff9800',
        fontSize: '2.5rem',
        filter: 'drop-shadow(0 2px 4px rgba(255, 152, 0, 0.3))'
    },
    infoIcon: {
        color: '#2196f3',
        fontSize: '2.5rem',
        filter: 'drop-shadow(0 2px 4px rgba(33, 150, 243, 0.3))'
    },
    loadingIcon: {
        color: '#667eea',
        filter: 'drop-shadow(0 2px 4px rgba(102, 126, 234, 0.3))'
    },
    // Countdown melhorado
    countdownText: {
        color: '#666',
        fontSize: '0.875rem',
        textAlign: 'center',
        marginTop: theme.spacing(0.5),
        fontWeight: '500',
        background: 'rgba(255, 152, 0, 0.1)',
        padding: theme.spacing(0.5, 1),
        borderRadius: '12px',
        border: '1px solid rgba(255, 152, 0, 0.2)'
    },
    // Input customizado
    emailInput: {
        position: 'relative',
        zIndex: 1,
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: 'rgba(248, 249, 250, 0.8)',
            position: 'relative',
            zIndex: 1,
            '& fieldset': {
                borderColor: 'rgba(0, 0, 0, 0.1)',
                zIndex: 1
            },
            '&:hover fieldset': {
                borderColor: 'rgba(102, 126, 234, 0.5)'
            },
            '&.Mui-focused fieldset': {
                borderColor: '#667eea',
                borderWidth: '2px'
            },
            '& input': {
                position: 'relative',
                zIndex: 2,
                cursor: 'text !important',
                pointerEvents: 'auto !important'
            }
        },
        '& .MuiInputLabel-root': {
            color: '#666',
            position: 'relative',
            zIndex: 1,
            '&.Mui-focused': {
                color: '#667eea'
            }
        },
        '& .MuiFormControl-root': {
            position: 'relative',
            zIndex: 1
        }
    },
    // Botões melhorados
    primaryButton: {
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: '#ffffff',
        borderRadius: '12px',
        padding: theme.spacing(1.5, 3),
        fontSize: '1rem',
        fontWeight: '600',
        textTransform: 'none',
        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
        border: 'none',
        transition: 'all 0.3s ease',
        '&:hover': {
            background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
            boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
            transform: 'translateY(-2px)'
        },
        '&:disabled': {
            background: '#ccc',
            boxShadow: 'none',
            transform: 'none'
        }
    },
    secondaryButton: {
        background: 'transparent',
        color: '#667eea',
        borderRadius: '12px',
        padding: theme.spacing(1, 2),
        fontSize: '0.9rem',
        fontWeight: '500',
        textTransform: 'none',
        border: '1px solid rgba(102, 126, 234, 0.3)',
        transition: 'all 0.3s ease',
        '&:hover': {
            background: 'rgba(102, 126, 234, 0.1)',
            borderColor: '#667eea'
        }
    },
    // Debug info melhorado
    debugInfo: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(1.5),
        background: 'rgba(248, 249, 250, 0.8)',
        borderRadius: '8px',
        fontSize: '0.75rem',
        border: '1px solid rgba(0, 0, 0, 0.1)',
        '& strong': {
            color: '#333',
            display: 'block',
            marginBottom: theme.spacing(0.5)
        },
        '& div': {
            color: '#666',
            marginBottom: theme.spacing(0.25)
        }
    },
    // Wrapper para centralização
    centerWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(1)
    },
    // Animação de loading
    loadingWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(1)
    }
}));// Estados da máquina de estados
const RECOVERY_STATES = {
    IDLE: 'idle',
    VALIDATING: 'validating',
    SENDING: 'sending',
    SUCCESS: 'success',
    ERROR: 'error',
    RATE_LIMITED: 'rate_limited'
};

// Códigos de erro do backend
const ERROR_CODES = {
    INVALID_EMAIL_FORMAT: 'INVALID_EMAIL_FORMAT',
    RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
    EMAIL_SEND_FAILED: 'EMAIL_SEND_FAILED',
    INTERNAL_ERROR: 'INTERNAL_ERROR',
    NETWORK_ERROR: 'NETWORK_ERROR'
};

function PasswordRecoveryModal({ open, onClose, onSuccess }) {
    const classes = useStyles();

    const [state, setState] = useState({
        email: '',
        currentState: RECOVERY_STATES.IDLE,
        error: null,
        success: false,
        countdown: 0,
        attempts: 0,
        lastAttempt: null,
        debugInfo: null
    });

    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'info'
    });

    // Countdown para rate limiting
    useEffect(() => {
        let interval = null;
        if (state.countdown > 0) {
            interval = setInterval(() => {
                setState(prev => ({
                    ...prev,
                    countdown: prev.countdown - 1
                }));
            }, 1000);
        } else if (state.currentState === RECOVERY_STATES.RATE_LIMITED) {
            setState(prev => ({
                ...prev,
                currentState: RECOVERY_STATES.IDLE
            }));
        }
        return () => clearInterval(interval);
    }, [state.countdown, state.currentState]);

    // Auto-focus no campo de email quando o modal abrir
    useEffect(() => {
        if (open) {
            // Pequeno delay para garantir que o DOM foi renderizado
            const timer = setTimeout(() => {
                const emailInput = document.getElementById('recovery-email');
                if (emailInput) {
                    emailInput.focus();
                }
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [open]);

    // Suporte para tecla ESC para fechar modal
    useEffect(() => {
        if (open) {
            const handleEscKey = (event) => {
                if (event.key === 'Escape') {
                    onClose();
                }
            };

            document.addEventListener('keydown', handleEscKey);
            return () => document.removeEventListener('keydown', handleEscKey);
        }
    }, [open, onClose]);

    // Validação de email em tempo real
    const isValidEmail = useCallback((email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }, []);

    // Handler para mudança de email
    const handleEmailChange = useCallback((value) => {
        setState(prev => ({
            ...prev,
            email: value,
            error: null
        }));
    }, []);

    // Função principal de recuperação
    const handlePasswordRecovery = useCallback(async () => {
        console.log('🚀 [Frontend] Starting password recovery process');

        // Log para desenvolvimento
        if (process.env.NODE_ENV === 'development') {
            console.log('🔧 [DEV MODE] Rate limiting desabilitado para desenvolvimento');
        }

        // Validações iniciais
        if (!state.email) {
            setNotification({
                open: true,
                message: 'Email é obrigatório',
                severity: 'warning'
            });
            return;
        }

        if (!isValidEmail(state.email)) {
            setState(prev => ({
                ...prev,
                error: {
                    code: ERROR_CODES.INVALID_EMAIL_FORMAT,
                    message: 'Formato de email inválido'
                }
            }));
            return;
        }

        // Verificar rate limiting local - apenas em produção
        const now = Date.now();
        if (process.env.NODE_ENV === 'production' && state.lastAttempt && (now - state.lastAttempt) < 300000) { // 5 minutos apenas em produção
            const remainingTime = Math.ceil((300000 - (now - state.lastAttempt)) / 1000);
            setState(prev => ({
                ...prev,
                currentState: RECOVERY_STATES.RATE_LIMITED,
                countdown: remainingTime,
                error: {
                    code: ERROR_CODES.RATE_LIMIT_EXCEEDED,
                    message: `Aguarde ${Math.ceil(remainingTime / 60)} minutos`
                }
            }));
            return;
        }

        setState(prev => ({
            ...prev,
            currentState: RECOVERY_STATES.VALIDATING,
            error: null
        }));

        try {
            setState(prev => ({ ...prev, currentState: RECOVERY_STATES.SENDING }));

            console.log(`🔄 [Frontend] Sending recovery request for: ${state.email}`);

            const response = await apims.get(`/M_User_RecoveryPass?email=${encodeURIComponent(state.email)}`);

            console.log('📨 [Frontend] Recovery response:', {
                status: response.status,
                data: response.data,
                headers: response.headers
            });

            if (response.data?.success === true) {
                console.log('✅ [Frontend] Recovery email sent successfully');

                setState(prev => ({
                    ...prev,
                    currentState: RECOVERY_STATES.SUCCESS,
                    attempts: prev.attempts + 1,
                    lastAttempt: now,
                    success: true,
                    debugInfo: {
                        timestamp: response.data.timestamp,
                        expiresIn: response.data.expiresIn,
                        message: response.data.message
                    }
                }));

                setNotification({
                    open: true,
                    message: 'Email de recuperação enviado! Verifique sua caixa de entrada e spam.',
                    severity: 'success'
                });

                if (onSuccess) {
                    setTimeout(() => onSuccess(response.data), 1500);
                }

            } else {
                // Se response.data?.success não for true, mas não houve erro HTTP
                const errorMessage = response.data?.error || response.data?.message || 'Falha ao processar solicitação de recuperação';
                throw new Error(errorMessage);
            }

        } catch (error) {
            console.error('❌ [Frontend] Recovery request failed:', error);

            let errorInfo = {
                code: ERROR_CODES.NETWORK_ERROR,
                message: 'Erro de conexão'
            };

            if (error.response?.data) {
                const backendError = error.response.data;
                errorInfo = {
                    code: backendError.code || ERROR_CODES.INTERNAL_ERROR,
                    message: backendError.error || 'Erro interno',
                    details: backendError
                };

                // Rate limiting do backend - apenas em produção
                if (process.env.NODE_ENV === 'production' && backendError.code === ERROR_CODES.RATE_LIMIT_EXCEEDED) {
                    setState(prev => ({
                        ...prev,
                        currentState: RECOVERY_STATES.RATE_LIMITED,
                        countdown: 300, // 5 minutos
                        lastAttempt: now
                    }));
                }
            }

            setState(prev => ({
                ...prev,
                currentState: RECOVERY_STATES.ERROR,
                error: errorInfo,
                attempts: prev.attempts + 1,
                lastAttempt: now
            }));

            setNotification({
                open: true,
                message: errorInfo.message,
                severity: 'error'
            });
        }
    }, [state.email, state.lastAttempt, isValidEmail, onSuccess]);

    // Reset do componente
    const handleReset = useCallback(() => {
        setState({
            email: '',
            currentState: RECOVERY_STATES.IDLE,
            error: null,
            success: false,
            countdown: 0,
            attempts: 0,
            lastAttempt: null,
            debugInfo: null
        });
    }, []);

    // Fechar notificação
    const handleCloseNotification = useCallback(() => {
        setNotification(prev => ({ ...prev, open: false }));
    }, []);

    // Render do ícone baseado no estado
    const renderStateIcon = () => {
        switch (state.currentState) {
            case RECOVERY_STATES.SUCCESS:
                return <CheckCircle className={classes.successIcon} />;
            case RECOVERY_STATES.ERROR:
                return <Error className={classes.errorIcon} />;
            case RECOVERY_STATES.RATE_LIMITED:
                return <Warning className={classes.warningIcon} />;
            case RECOVERY_STATES.SENDING:
            case RECOVERY_STATES.VALIDATING:
                return <CircularProgress size={40} className={classes.loadingIcon} />;
            default:
                return <Info className={classes.infoIcon} />;
        }
    };

    // Render da mensagem de estado
    const renderStateMessage = () => {
        switch (state.currentState) {
            case RECOVERY_STATES.VALIDATING:
                return (
                    <div className={classes.stateMessage}>
                        Validando seu email...
                    </div>
                );
            case RECOVERY_STATES.SENDING:
                return (
                    <div className={classes.stateMessage}>
                        Enviando email de recuperação...
                        <div style={{ fontSize: '0.8rem', marginTop: 8, opacity: 0.7 }}>
                            Isso pode levar alguns segundos
                        </div>
                    </div>
                );
            case RECOVERY_STATES.SUCCESS:
                return (
                    <div className={classes.stateMessage}>
                        <div style={{ fontSize: '1.1rem', fontWeight: '600', color: '#4caf50', marginBottom: 8 }}>
                            Email enviado com sucesso! 📧
                        </div>
                        <div style={{ marginBottom: 12 }}>
                            Se você possui uma conta conosco, receberá um email de <strong>@machen.global</strong> com as instruções para recuperar sua senha.
                        </div>
                        <div style={{
                            backgroundColor: 'rgba(255, 193, 7, 0.1)',
                            border: '1px solid rgba(255, 193, 7, 0.3)',
                            borderRadius: '8px',
                            padding: '12px',
                            fontSize: '0.9rem',
                            marginBottom: 8
                        }}>
                            <div style={{ fontWeight: '600', color: '#ff6f00', marginBottom: 4 }}>
                                ⚠️ Importante:
                            </div>
                            <div>
                                • Verifique sua <strong>caixa de entrada</strong><br />
                                • Verifique a pasta de <strong>spam/lixo eletrônico</strong><br />
                                • O email pode levar alguns minutos para chegar
                            </div>
                        </div>
                        {state.debugInfo && (
                            <div style={{ marginTop: 12, fontSize: '0.85rem', color: '#666', fontWeight: '500' }}>
                                ⏱️ Link válido por: {state.debugInfo.expiresIn || '1 hora'}
                            </div>
                        )}
                    </div>
                );
            case RECOVERY_STATES.RATE_LIMITED:
                return (
                    <div className={classes.stateMessage}>
                        <div style={{ fontSize: '1rem', fontWeight: '600', color: '#ff9800', marginBottom: 8 }}>
                            Muitas tentativas detectadas
                        </div>
                        <div style={{ marginBottom: 8 }}>
                            Por favor, aguarde antes de tentar novamente
                        </div>
                        <div className={classes.countdownText}>
                            ⏰ Tente novamente em {Math.floor(state.countdown / 60)}:{String(state.countdown % 60).padStart(2, '0')}
                        </div>
                    </div>
                );
            case RECOVERY_STATES.ERROR:
                return (
                    <div className={classes.stateMessage}>
                        <div style={{ fontSize: '1rem', fontWeight: '600', color: '#f44336', marginBottom: 8 }}>
                            Oops! Algo deu errado
                        </div>
                        <div style={{ marginBottom: 8 }}>
                            {state.error?.message || 'Erro ao enviar email de recuperação'}
                        </div>
                        {state.error?.code && (
                            <div style={{ fontSize: '0.75rem', opacity: 0.7, fontFamily: 'monospace' }}>
                                Código: {state.error.code}
                            </div>
                        )}
                    </div>
                );
            default:
                return (
                    <div className={classes.stateMessage}>
                        <div style={{ fontSize: '1rem', marginBottom: 8 }}>
                            Recuperação de Senha
                        </div>
                        <div>
                            Digite seu email para receber as instruções de recuperação de senha do domínio <strong>@machen.global</strong>
                        </div>
                    </div>
                );
        }
    }; const isLoading = [RECOVERY_STATES.VALIDATING, RECOVERY_STATES.SENDING].includes(state.currentState);
    const isDisabled = isLoading ||
        (process.env.NODE_ENV === 'production' && state.currentState === RECOVERY_STATES.RATE_LIMITED) ||
        !isValidEmail(state.email);

    // Não renderizar se o modal não estiver aberto
    if (!open) return null;

    return (
        <>
            <div
                className={classes.recoveryContainer}
                onClick={(e) => {
                    // Fechar modal se clicar no backdrop
                    if (e.target === e.currentTarget) {
                        onClose();
                    }
                }}
            >
                <div
                    className={classes.recoveryCard}
                    onClick={(e) => e.stopPropagation()} // Previne o fechamento ao clicar no card
                >
                    {/* Header */}
                    <div className={classes.recoveryHeader}>
                        <h4 className={classes.recoveryTitle}>
                            Recuperar Senha
                            {process.env.NODE_ENV === 'development' && (
                                <span style={{
                                    fontSize: '0.7rem',
                                    backgroundColor: '#4caf50',
                                    color: 'white',
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    marginLeft: '8px',
                                    fontWeight: '400'
                                }}>
                                    DEV
                                </span>
                            )}
                        </h4>
                        <div className={classes.recoverySubtitle}>
                            {process.env.NODE_ENV === 'development' ?
                                'Modo desenvolvimento - sem limite de tentativas' :
                                'Processo seguro e rápido'
                            }
                        </div>
                    </div>

                    {/* Body */}
                    <div className={classes.recoveryBody}>
                        {/* Indicador de estado */}
                        <div className={classes.stepIndicator}>
                            {renderStateIcon()}
                            {renderStateMessage()}
                        </div>

                        {/* Campo de email */}
                        {state.currentState !== RECOVERY_STATES.SUCCESS && (
                            <div style={{
                                marginBottom: 24,
                                position: 'relative',
                                zIndex: 1,
                                pointerEvents: 'auto'
                            }}>
                                <CustomInput
                                    labelText="Endereço de Email"
                                    id="recovery-email"
                                    formControlProps={{
                                        fullWidth: true,
                                        className: classes.emailInput,
                                        error: !!state.error && state.error.code === ERROR_CODES.INVALID_EMAIL_FORMAT,
                                        style: {
                                            position: 'relative',
                                            zIndex: 1,
                                            pointerEvents: 'auto'
                                        }
                                    }}
                                    inputProps={{
                                        value: state.email,
                                        onChange: (e) => handleEmailChange(e.target.value),
                                        onKeyDown: (e) => e.keyCode === 13 && !isDisabled && handlePasswordRecovery(),
                                        disabled: isLoading,
                                        placeholder: 'Digite seu email...',
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Email className={classes.inputAdornmentIcon} style={{ color: '#667eea' }} />
                                            </InputAdornment>
                                        ),
                                        type: "email",
                                        autoComplete: "email",
                                        style: {
                                            fontSize: '1rem',
                                            position: 'relative',
                                            zIndex: 2,
                                            pointerEvents: 'auto',
                                            cursor: 'text'
                                        }
                                    }}
                                />
                                {state.error && state.error.code === ERROR_CODES.INVALID_EMAIL_FORMAT && (
                                    <div style={{ color: '#f44336', fontSize: '0.8rem', marginTop: 8 }}>
                                        {state.error.message}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Debug info para desenvolvimento */}
                        {process.env.NODE_ENV === 'development' && state.attempts > 0 && (
                            <div className={classes.debugInfo}>
                                <strong>🐛 Debug Info:</strong>
                                <div>Tentativas: {state.attempts}</div>
                                <div>Estado: {state.currentState}</div>
                                {state.lastAttempt && (
                                    <div>Última tentativa: {new Date(state.lastAttempt).toLocaleTimeString()}</div>
                                )}
                                {state.debugInfo && (
                                    <div>Timestamp: {state.debugInfo.timestamp}</div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className={classes.recoveryFooter}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {state.currentState !== RECOVERY_STATES.SUCCESS ? (
                                <Button
                                    className={classes.primaryButton}
                                    fullWidth
                                    size="large"
                                    onClick={handlePasswordRecovery}
                                    disabled={isDisabled}
                                >
                                    <div className={classes.loadingWrapper}>
                                        {isLoading ? (
                                            <>
                                                <CircularProgress size={20} style={{ color: "#FFF" }} />
                                                Enviando...
                                            </>
                                        ) : (
                                            'Enviar Email de Recuperação'
                                        )}
                                    </div>
                                </Button>
                            ) : (
                                <Button
                                    className={classes.primaryButton}
                                    fullWidth
                                    size="large"
                                    onClick={handleReset}
                                >
                                    📧 Enviar Novamente
                                </Button>
                            )}

                            <Button
                                className={classes.secondaryButton}
                                fullWidth
                                size="medium"
                                onClick={onClose}
                            >
                                ← Voltar ao Login
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Notificações */}
            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseNotification}
                    severity={notification.severity}
                    variant="filled"
                    style={{ borderRadius: '12px' }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </>
    );
}

export default PasswordRecoveryModal;
